import { keyBy } from 'lodash';
import { Observable, catchError, map, retry, tap, timer, toArray } from 'rxjs';
import type { MonoTypeOperatorFunction, OperatorFunction } from 'rxjs';

import { log } from './logging';

let subscription = 0;

export function logged<T>(name: string) {
  return (source: Observable<T>): Observable<T> => {
    const prefix = `${name} ${subscription++}`;

    log(prefix, 'subscribed');
    return new Observable<T>((subscriber) => {
      let completed = false;

      const subscription = source.subscribe({
        next: (item) => {
          log(prefix, 'new item', item);
          subscriber.next(item);
        },
        error: (error) => {
          log(prefix, 'error', error);
          completed = true;
          subscriber.error(error);
        },
        complete: () => {
          log(prefix, 'completed');
          completed = true;
          subscriber.complete();
        },
      });

      return (): void => {
        if (!completed) {
          log(prefix, 'cancelled');
        }

        log(prefix, 'disposed');
        subscription.unsubscribe();
      };
    });
  };
}

export function toDictionary<T>(
  keySelector: (item: T) => string
): OperatorFunction<T, Record<string, T>> {
  return (source: Observable<T>) =>
    source.pipe(
      toArray(),
      map((array: T[]) => keyBy(array, keySelector))
    );
}

export function retryHandled<T>(options: {
  count?: number;
  delay?: number;
  onStart?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  onRetry?: (retryCount: number) => void;
}): MonoTypeOperatorFunction<T> {
  let hasRetried = false;

  return (source: Observable<T>): Observable<T> =>
    source.pipe(
      retry({
        count: options.count,
        delay: (_, retryCount) => {
          options.onRetry?.(retryCount);
          if (retryCount === 1) {
            hasRetried = true;
            options.onStart?.();
          }

          return timer(options.delay ?? 0);
        },
      }),
      tap(() => {
        if (hasRetried) {
          options.onSuccess?.();
        }
      }),
      catchError((error) => {
        if (hasRetried) {
          options.onError?.();
        }

        throw error;
      })
    );
}
